/**
 * BinManagePage - Coordinator bin management page
 *
 * Scanner-first page for inspecting bins, releasing stuck bins,
 * and moving orders from regular bins to OOS bins.
 *
 * Flow:
 * 1. Scan a bin QR or click a bin in the grid to inspect it
 * 2. See bin details: order info, garment list with images, status
 * 3. Actions: "Move to OOS Bin" (opens secondary scan), "Release Bin" (confirmation)
 * 4. Move-to-OOS sub-flow: scan an EMPTY OOS bin → order moves, source bin freed
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  ArrowLeft,
  ArrowRightLeft,
  Package,
  Unlock,
  QrCode,
  Loader2,
  CheckCircle2,
  RefreshCw,
  ImageOff,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useLocation } from '@/contexts/LocationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/trpc';
import { detectQRType, extractBinLabel } from '@/lib/qr-utils';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { useOrganization } from '@/contexts/OrganizationContext';
import { formatOrderNumber, stripOrderNamePrefix } from '@threadheads/shared';
import { cn } from '@/lib/utils';
import { ExpressBadge } from './ExpressBadge';
import { ZoomImg } from '@/components/production/HoverZoom';

// ============================================================================
// STATUS BADGE STYLES
// ============================================================================

const STATUS_BADGE_VARIANT: Record<string, string> = {
  EMPTY: 'bg-muted text-muted-foreground',
  IN_USE: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  READY_TO_PACK: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  HELD: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  OCCUPIED: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  READY_TO_RESUME: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  CUSTOM_ISSUE: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

const GARMENT_STATUS_STYLE: Record<string, string> = {
  CAUGHT: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  IN_BIN: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  PRINTED: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
  // Phase 50.01.5 — DTF (matches Tracker badges); DTF garments rarely reach bin
  // views pre-catch, but render styled if they do (never unstyled).
  FILM_PRINTED: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  PRESSED: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-200',
  LABEL_PRINTED: 'bg-gray-100 text-gray-800 dark:bg-gray-800/40 dark:text-gray-200',
  PICKED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

type PageMode = 'browse' | 'detail' | 'oos-scan';

export function BinManagePage() {
  const { t } = useTranslation('production');
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const shopifyDomain = organization?.shopify_domain ?? null;
  const [mode, setMode] = useState<PageMode>('browse');
  const [selectedBinId, setSelectedBinId] = useState<string | null>(null);
  const [sourceBinIdForOos, setSourceBinIdForOos] = useState<string | null>(null);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const scannerRef = useRef<HTMLInputElement>(null);
  const oosScannerRef = useRef<HTMLInputElement>(null);

  const utils = trpc.useUtils();
  const locationId = selectedLocation?.id;

  // Fetch all bins for grid (both REGULAR and OOS)
  const regularBinsQuery = trpc.bins.getLocationBins.useQuery(
    locationId ? { locationId, binType: 'REGULAR' as const } : undefined!,
    { enabled: !!locationId, staleTime: 15_000, refetchInterval: 15_000 }
  );
  const oosBinsQuery = trpc.bins.getLocationBins.useQuery(
    locationId ? { locationId, binType: 'OOS' as const } : undefined!,
    { enabled: !!locationId, staleTime: 15_000, refetchInterval: 15_000 }
  );

  // Fetch bin details when selected
  const binDetailsQuery = trpc.bins.getBinDetails.useQuery(
    locationId && selectedBinId ? { locationId, binId: selectedBinId } : undefined!,
    { enabled: !!locationId && !!selectedBinId, staleTime: 5_000 }
  );

  // Mutations
  const moveToOosMutation = trpc.bins.moveToOosBin.useMutation({
    onSuccess: (result) => {
      toast.success(t('bins.movedToOos', { orderName: result.orderName, targetBin: result.targetBinLabel }), {
        description: t('bins.sourceBinEmpty', { sourceBin: result.sourceBinLabel }),
      });
      invalidateAll();
      setMode('browse');
      setSelectedBinId(null);
      setSourceBinIdForOos(null);
    },
    onError: (error) => {
      toast.error(t('bins.failedMoveOos'), { description: error.message });
    },
  });

  const releaseBinMutation = trpc.bins.releaseBin.useMutation({
    onSuccess: (result) => {
      toast.success(t('bins.releasedBin', { binLabel: result.binLabel }), {
        description: result.orderName ? t('bins.orderUnlinked', { orderName: result.orderName }) : t('bins.binNowEmpty'),
      });
      invalidateAll();
      setMode('browse');
      setSelectedBinId(null);
      setShowReleaseConfirm(false);
    },
    onError: (error) => {
      toast.error(t('bins.failedReleaseBin'), { description: error.message });
      setShowReleaseConfirm(false);
    },
  });

  const invalidateAll = useCallback(() => {
    utils.bins.getLocationBins.invalidate();
    utils.bins.getBinDetails.invalidate();
    utils.bins.getBinOccupancy.invalidate();
  }, [utils]);

  // Focus scanner on mount and mode changes
  useEffect(() => {
    if (mode === 'browse') {
      setTimeout(() => scannerRef.current?.focus(), 100);
    } else if (mode === 'oos-scan') {
      setTimeout(() => oosScannerRef.current?.focus(), 100);
    }
  }, [mode]);

  // ============================================================================
  // SCAN HANDLERS
  // ============================================================================

  const handleBinScan = useCallback((value: string) => {
    if (!locationId) return;

    let label: string | null = null;

    // Try QR format first
    if (value.startsWith('THP://')) {
      const qrType = detectQRType(value);
      if (qrType !== 'bin') {
        toast.error(t('bins.notBinQr'), { description: t('bins.scanBinQrPlease') });
        return;
      }
      label = extractBinLabel(value);
    } else {
      // Raw label input (typed)
      label = value.trim().toUpperCase();
    }

    if (!label) {
      toast.error(t('bins.invalidBinLabel'));
      return;
    }

    // Find matching bin from cached data
    const allBins = [...(regularBinsQuery.data ?? []), ...(oosBinsQuery.data ?? [])];
    const matchedBin = allBins.find(b => b.label.toUpperCase() === label.toUpperCase());

    if (matchedBin) {
      setSelectedBinId(matchedBin.id);
      setMode('detail');
    } else {
      toast.error(t('bins.binNotFound', { label }));
    }
  }, [locationId, regularBinsQuery.data, oosBinsQuery.data]);

  const handleOosBinScan = useCallback((value: string) => {
    if (!locationId || !sourceBinIdForOos) return;

    let label: string | null = null;

    if (value.startsWith('THP://')) {
      const qrType = detectQRType(value);
      if (qrType !== 'bin') {
        toast.error(t('bins.notBinQr'), { description: t('bins.scanOosBinQrPlease') });
        return;
      }
      label = extractBinLabel(value);
    } else {
      label = value.trim().toUpperCase();
    }

    if (!label) {
      toast.error(t('bins.invalidBinLabel'));
      return;
    }

    // Find matching OOS bin
    const oosBin = (oosBinsQuery.data ?? []).find(
      b => b.label.toUpperCase() === label.toUpperCase()
    );

    if (!oosBin) {
      toast.error(t('bins.oosBinNotFound', { label }));
      return;
    }

    if (oosBin.status !== 'EMPTY') {
      toast.error(t('bins.binNotEmpty', { label: oosBin.label }), { description: `Status: ${oosBin.status}` });
      return;
    }

    // Execute move
    moveToOosMutation.mutate({
      locationId,
      sourceBinId: sourceBinIdForOos,
      targetOosBinId: oosBin.id,
    });
  }, [locationId, sourceBinIdForOos, oosBinsQuery.data, moveToOosMutation]);

  const handleScannerSubmit = useCallback((e: React.FormEvent, handler: (v: string) => void, ref: React.RefObject<HTMLInputElement | null>) => {
    e.preventDefault();
    const value = ref.current?.value.trim();
    if (value) {
      handler(value);
      if (ref.current) ref.current.value = '';
    }
  }, []);

  const handleScannerKeyDown = useCallback((e: React.KeyboardEvent, handler: (v: string) => void, ref: React.RefObject<HTMLInputElement | null>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = ref.current?.value.trim();
      if (value) {
        handler(value);
        if (ref.current) ref.current.value = '';
      }
    }
  }, []);

  // ============================================================================
  // CLICK HANDLERS
  // ============================================================================

  const handleBinClick = useCallback((binId: string) => {
    setSelectedBinId(binId);
    setMode('detail');
  }, []);

  const handleMoveToOos = useCallback(() => {
    if (selectedBinId) {
      setSourceBinIdForOos(selectedBinId);
      setMode('oos-scan');
    }
  }, [selectedBinId]);

  const handleRelease = useCallback(() => {
    setShowReleaseConfirm(true);
  }, []);

  const confirmRelease = useCallback(() => {
    if (!locationId || !selectedBinId) return;
    releaseBinMutation.mutate({ locationId, binId: selectedBinId });
  }, [locationId, selectedBinId, releaseBinMutation]);

  const handleBack = useCallback(() => {
    setMode('browse');
    setSelectedBinId(null);
    setSourceBinIdForOos(null);
  }, []);

  // ============================================================================
  // GUARDS
  // ============================================================================

  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Archive className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">{t('bins.selectLocation')}</p>
            <p className="text-muted-foreground">{t('bins.selectLocationManage')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  const detail = binDetailsQuery.data;
  const isLoading = moveToOosMutation.isPending || releaseBinMutation.isPending;

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {mode !== 'browse' && (
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div className="p-2 rounded-lg bg-primary/10">
            <Archive className="h-6 w-6 text-primary" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">
              {mode === 'oos-scan' ? t('bins.scanOosBinTitle') : t('bins.manageBins')}
            </h1>
            <p className="text-muted-foreground">
              {mode === 'oos-scan'
                ? t('bins.scanOosBinDesc')
                : mode === 'detail'
                  ? t('bins.inspectBinContents')
                  : t('bins.scanOrClickBin')}
            </p>
          </div>
        </div>
        <Button variant="outline" asChild>
          <Link to="/production/bins">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('bins.dashboard')}
          </Link>
        </Button>
      </div>

      {/* === BROWSE MODE === */}
      {mode === 'browse' && (
        <>
          {/* Scanner input */}
          <Card>
            <CardContent className="pt-6">
              <form
                onSubmit={(e) => handleScannerSubmit(e, handleBinScan, scannerRef)}
                className="relative"
              >
                <div className="relative">
                  <QrCode className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    data-scanner-input
                    ref={scannerRef}
                    placeholder="Scan bin QR code or type label..."
                    className="h-16 text-lg pl-12 font-mono"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    onKeyDown={(e) => handleScannerKeyDown(e, handleBinScan, scannerRef)}
                  />
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Bin grids - split by size */}
          {(() => {
            const allRegular = regularBinsQuery.data ?? [];
            const standardBins = allRegular.filter(b => !b.binSize || b.binSize === 'STANDARD');
            const largeBins = allRegular.filter(b => b.binSize === 'LARGE');
            const hugeBins = allRegular.filter(b => b.binSize === 'HUGE');

            return (
              <>
                {/* Standard Bins */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{t('bins.standardBins')}</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {regularBinsQuery.isLoading ? (
                      <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                        {Array.from({ length: 20 }).map((_, i) => (
                          <div key={i} className="h-14 rounded-lg bg-muted animate-pulse" />
                        ))}
                      </div>
                    ) : standardBins.length > 0 ? (
                      <ClickableBinGrid
                        bins={standardBins}
                        onBinClick={handleBinClick}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        {t('bins.noStandardBins')}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Large Bins */}
                {largeBins.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">{t('bins.largeBins')}</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ClickableBinGrid
                        bins={largeBins}
                        onBinClick={handleBinClick}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Huge Bins */}
                {hugeBins.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">{t('bins.hugeBins')}</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ClickableBinGrid
                        bins={hugeBins}
                        onBinClick={handleBinClick}
                      />
                    </CardContent>
                  </Card>
                )}
              </>
            );
          })()}

          {/* Bin grid - OOS */}
          {(oosBinsQuery.data?.length ?? 0) > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{t('bins.oosBins')}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ClickableBinGrid
                  bins={oosBinsQuery.data!}
                  onBinClick={handleBinClick}
                />
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* === DETAIL MODE === */}
      {mode === 'detail' && (
        <>
          {binDetailsQuery.isLoading ? (
            <Card>
              <CardContent className="py-12 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </CardContent>
            </Card>
          ) : !detail ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">{t('bins.binNotFoundDetail')}</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Bin info header */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl font-bold font-mono">{detail.bin.label}</span>
                      <Badge className={cn('text-xs', STATUS_BADGE_VARIANT[detail.bin.status] ?? 'bg-muted')}>
                        {detail.bin.status.replace(/_/g, ' ')}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {detail.bin.binType}
                      </Badge>
                      {detail.bin.binSize !== 'STANDARD' && (
                        <Badge variant="outline" className="text-xs">
                          {detail.bin.binSize}
                        </Badge>
                      )}
                    </div>
                    {detail.bin.assignedAt && (
                      <span className="text-sm text-muted-foreground">
                        Assigned {formatTimeAgo(detail.bin.assignedAt)}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Order details */}
              {detail.order ? (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        {(() => {
                          const url = getShopifyAdminOrderUrl(shopifyDomain, detail.order.shopifyOrderId);
                          return url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              {formatOrderNumber(detail.order.orderName)}
                            </a>
                          ) : (
                            formatOrderNumber(detail.order.orderName)
                          );
                        })()}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge className={cn('text-xs', STATUS_BADGE_VARIANT[detail.order.productionStatus] ?? 'bg-muted')}>
                          {detail.order.productionStatus.replace(/_/g, ' ')}
                        </Badge>
                        {detail.order.hasRedoPending && (
                          <Badge className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Redo Pending
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-4">
                    {detail.order.customerName && (
                      <p className="text-sm text-muted-foreground">
                        {t('bins.customer', { name: detail.order.customerName })}
                      </p>
                    )}

                    {/* Progress bar */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>{t('bins.caught')}</span>
                        <span className="font-medium">
                          {detail.order.itemsCaught} / {detail.order.totalItems}
                        </span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${detail.order.hasRedoPending ? 'bg-amber-500' : 'bg-emerald-500'}`}
                          style={{
                            width: `${detail.order.totalItems > 0
                              ? Math.min(100, Math.round((detail.order.itemsCaught / detail.order.totalItems) * 100))
                              : 0}%`
                          }}
                        />
                      </div>
                    </div>

                    {/* Garment list */}
                    {detail.garments.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium">{t('bins.items', { count: detail.garments.length })}</p>
                        <div className="divide-y rounded-lg border">
                          {detail.garments.map(garment => (
                            <div key={garment.id} className="flex items-center gap-3 p-3">
                              {/* Thumbnail */}
                              <div className="h-12 w-12 rounded-md bg-muted flex items-center justify-center overflow-hidden shrink-0">
                                {garment.imageUrl ? (
                                  <ZoomImg
                                    src={garment.imageUrl}
                                    alt={garment.productName}
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                  />
                                ) : (
                                  <ImageOff className="h-5 w-5 text-muted-foreground" />
                                )}
                              </div>
                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{garment.productName || garment.sku}</p>
                                <p className="text-xs text-muted-foreground font-mono">{garment.sku}</p>
                              </div>
                              {/* Status + redo badge */}
                              <div className="flex items-center gap-2 shrink-0">
                                {garment.isRedo && (
                                  garment.redoReason === 'order_change_backfill' ? (
                                    <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 dark:border-amber-600 dark:text-amber-300">
                                      <Plus className="h-3 w-3 mr-1" />
                                      {t('orderChange.badge')}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-xs">
                                      <RefreshCw className="h-3 w-3 mr-1" />
                                      Redo
                                    </Badge>
                                  )
                                )}
                                <Badge className={cn('text-xs', GARMENT_STATUS_STYLE[garment.status] ?? 'bg-muted text-muted-foreground')}>
                                  {garment.status.replace(/_/g, ' ')}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-3 pt-2">
                      {detail.bin.binType === 'REGULAR' && detail.bin.status !== 'EMPTY' && detail.order && (
                        <Button
                          onClick={handleMoveToOos}
                          disabled={isLoading}
                          className="bg-orange-600 hover:bg-orange-700"
                        >
                          <ArrowRightLeft className="h-4 w-4 mr-2" />
                          {t('bins.moveToOosBin')}
                        </Button>
                      )}
                      {detail.bin.status !== 'EMPTY' && (
                        <Button
                          variant="destructive"
                          onClick={handleRelease}
                          disabled={isLoading}
                        >
                          <Unlock className="h-4 w-4 mr-2" />
                          {t('bins.releaseBin')}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-8 text-center">
                    <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-lg font-medium">{t('bins.binIsEmpty')}</p>
                    <p className="text-sm text-muted-foreground">{t('bins.noOrderAssigned')}</p>
                    {detail.bin.status !== 'EMPTY' && (
                      <Button
                        variant="destructive"
                        onClick={handleRelease}
                        disabled={isLoading}
                        className="mt-4"
                      >
                        <Unlock className="h-4 w-4 mr-2" />
                        {t('bins.forceResetEmpty')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </>
      )}

      {/* === OOS SCAN MODE === */}
      {mode === 'oos-scan' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-orange-600" />
              {t('bins.scanTargetOosBin')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('bins.scanOosBinDesc')}
            </p>
            <form
              onSubmit={(e) => handleScannerSubmit(e, handleOosBinScan, oosScannerRef)}
              className="relative"
            >
              <div className="relative">
                <QrCode className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  data-scanner-input
                  ref={oosScannerRef}
                  placeholder="Scan OOS bin QR code..."
                  disabled={moveToOosMutation.isPending}
                  className="h-16 text-lg pl-12 font-mono"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  onKeyDown={(e) => handleScannerKeyDown(e, handleOosBinScan, oosScannerRef)}
                />
                {moveToOosMutation.isPending && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}
              </div>
            </form>

            {/* Quick-select OOS bins that are empty */}
            {(() => {
              const emptyOosBins = (oosBinsQuery.data ?? []).filter(b => b.status === 'EMPTY');
              if (emptyOosBins.length === 0) return (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  {t('bins.noEmptyOosBins')}
                </p>
              );
              return (
                <div className="space-y-2">
                  <p className="text-sm font-medium">{t('bins.orClickEmptyOosBin')}</p>
                  <div className="flex flex-wrap gap-2">
                    {emptyOosBins.map(bin => (
                      <Button
                        key={bin.id}
                        variant="outline"
                        size="lg"
                        disabled={moveToOosMutation.isPending}
                        onClick={() => {
                          if (!locationId || !sourceBinIdForOos) return;
                          moveToOosMutation.mutate({
                            locationId,
                            sourceBinId: sourceBinIdForOos,
                            targetOosBinId: bin.id,
                          });
                        }}
                        className="font-mono text-lg h-14 px-6"
                      >
                        {bin.label}
                      </Button>
                    ))}
                  </div>
                </div>
              );
            })()}

            <Button variant="outline" onClick={handleBack} disabled={moveToOosMutation.isPending}>
              {t('bins.cancel')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Release confirmation dialog */}
      <AlertDialog open={showReleaseConfirm} onOpenChange={setShowReleaseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bins.releaseBinConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {detail?.order
                ? t('bins.releaseBinOrderDesc', { orderName: detail.order.orderName, binLabel: detail.bin.label })
                : t('bins.releaseBinForceDesc', { binLabel: detail?.bin.label ?? 'the bin' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={releaseBinMutation.isPending}>{t('bins.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRelease}
              disabled={releaseBinMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {releaseBinMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Unlock className="h-4 w-4 mr-2" />
              )}
              {t('bins.releaseBin')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// CLICKABLE BIN GRID
// ============================================================================

const BIN_STATUS_STYLES = {
  EMPTY: 'bg-muted text-muted-foreground',
  IN_USE: 'bg-orange-100 text-orange-900 dark:bg-orange-900/30 dark:text-orange-200',
  READY_TO_PACK: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200',
  HELD: 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200',
  OCCUPIED: 'bg-orange-100 text-orange-900 dark:bg-orange-900/30 dark:text-orange-200',
  READY_TO_RESUME: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200',
  CUSTOM_ISSUE: 'bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200',
} as const;

// Match BinCell overrides so dashboard and management grids use identical colors
const REDO_PENDING_STYLE = 'bg-amber-200 text-amber-900 border-l-[3px] border-l-amber-500 dark:bg-amber-800/40 dark:text-amber-100 dark:border-l-amber-400';
const STUCK_STYLE = 'bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200';

interface ClickableBinGridProps {
  bins: Array<{
    id: string;
    label: string;
    status: string;
    orderName?: string | null;
    binSize?: string;
    isStuck?: boolean;
    exceedsThreshold?: boolean;
    isExpress?: boolean | null;
    hasRedoPending?: boolean;
    itemsCaught?: number;
    totalItems?: number;
    timeInBinMs?: number | null;
  }>;
  onBinClick: (binId: string) => void;
}

function ClickableBinGrid({ bins, onBinClick }: ClickableBinGridProps) {
  const { t } = useTranslation('production');
  return (
    <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
      {bins.map(bin => {
        const num = extractBinNumber(bin.label);
        const showExpress = bin.status !== 'EMPTY' && bin.isExpress === true;
        // BLU-338: Use count-based guard matching BinCell (suppress when all items caught = stale flag)
        const totalItems = bin.totalItems ?? 0;
        const itemsCaught = bin.itemsCaught ?? 0;
        const effectiveRedoPending = bin.hasRedoPending && totalItems > 0 && itemsCaught < totalItems;
        const style = bin.isStuck ? STUCK_STYLE
          : effectiveRedoPending ? REDO_PENDING_STYLE
          : BIN_STATUS_STYLES[bin.status as keyof typeof BIN_STATUS_STYLES] ?? 'bg-muted text-muted-foreground';
        const timeDisplay = formatDurationCompact(bin.timeInBinMs);
        return (
          <button
            key={bin.id}
            type="button"
            onClick={() => onBinClick(bin.id)}
            className={cn(
              'rounded-lg flex flex-col items-center justify-center transition-all py-1.5',
              'h-20',
              'hover:ring-2 hover:ring-primary hover:ring-offset-1 cursor-pointer',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
              style
            )}
            aria-label={`${bin.label}, ${bin.status.toLowerCase().replace(/_/g, ' ')}${bin.orderName ? `, ${bin.orderName}` : ''}${showExpress ? `, ${t('bins.expressLabel')}` : ''}`}
            title={`${bin.label}${bin.orderName ? `: ${bin.orderName}` : ''}${timeDisplay ? ` (${timeDisplay})` : ''}`}
          >
            <span className="text-xl font-bold leading-none">{num}</span>
            {bin.status !== 'EMPTY' && timeDisplay ? (
              <span className="text-[10px] opacity-75 mt-0.5 max-w-full truncate px-1">
                {timeDisplay}
              </span>
            ) : bin.orderName ? (
              <span className="text-[9px] opacity-60 max-w-full truncate px-1">
                {stripOrderNamePrefix(bin.orderName)}
              </span>
            ) : null}
            {showExpress && <ExpressBadge size="sm" className="mt-1" />}
          </button>
        );
      })}
    </div>
  );
}

function extractBinNumber(label: string): string {
  const match = label.match(/^(.+)-(\d+)$/);
  if (!match) return label;
  const prefix = match[1]!;
  const num = match[2]!.replace(/^0+/, '') || match[2]!;
  if (prefix === 'BIN') return num;
  if (prefix === 'OOS') return `O${num}`;
  if (prefix === 'LRG') return `L${num}`;
  if (prefix === 'HGE') return `H${num}`;
  return label;
}

function formatDurationCompact(ms: number | null | undefined): string {
  if (!ms || ms < 0) return '';
  const minutes = Math.floor(ms / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

function formatTimeAgo(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime();
  if (ms < 0) return 'just now';
  const minutes = Math.floor(ms / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h ago`;
  if (hours > 0) return `${hours}h ${minutes % 60}m ago`;
  return `${minutes}m ago`;
}
